import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {useHistory} from "react-router";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    publishAnalyticsEventForMinorConversion,
    usePublishAnalyticsEvent,
    withConversionContext,
} from "@khan/analytics-learning-time";
import {useMutation} from "@khan/data-gql";
import {useCoeditorAPIContext} from "@khan/guide-coeditor-core";
import {Coeditor, type RestorableCoeditingSnapshot} from "@khan/guide-ui";
import {type ToolPersona} from "@khan/khanmigo-tools";
import {PermissionsInfo} from "@khan/shared-core";
import {reload, useLocation} from "@khan/urls";

import AIToolHeader from "#coach-dashboard-package/khanmigo/ai-tool-header.tsx";
import {TOOL_MAX_WIDTH} from "#coach-dashboard-package/khanmigo/ai-tool-page.tsx";
import type {Tool} from "#coach-dashboard-package/khanmigo/ai-tools-list.ts";
import {COPY_COEDITING_DOCUMENT_MUTATION} from "#coach-dashboard-package/khanmigo/mutations.ts";
import {Platform} from "#coach-dashboard-package/khanmigo/utils.ts";
import {createResumeDocumentHref} from "#coach-dashboard-package/resource-tab/utils.ts";
import {TeacherToolsSignupPopup} from "#khanmigo-package/teacher-tools-signup-popup.tsx";
import {renderErrorToast} from "#notifications-package/toast-notification.tsx";

type Props = {
    tool: Tool;
    accessibleToolConfigNames: string[];
    isKhanmigoEligible: boolean;
    documentID: string;
    snapshotID: string;
    snapshot: RestorableCoeditingSnapshot;
    persona: ToolPersona;
};

const TeacherDocumentView = (props: Props): React.ReactElement => {
    const {
        tool,
        accessibleToolConfigNames,
        isKhanmigoEligible,
        documentID,
        snapshotID,
        snapshot,
        persona,
    } = props;
    const {restoreSnapshot} = useCoeditorAPIContext();

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    React.useEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideTeacherDocumentViewed",
            eventSchemaVersion: 1,
            documentId: documentID,
            aiGuideExperienceReportingLabelId: tool.reportingLabelId,
        });
    }, [documentID, publishAnalyticsEvent, tool.reportingLabelId]);

    React.useEffect(() => {
        if (snapshot) {
            restoreSnapshot(snapshot);
        }
    }, [snapshot, restoreSnapshot]);

    const [copyDocumentMutation] = useMutation(
        COPY_COEDITING_DOCUMENT_MUTATION,
    );

    const history = useHistory();

    const [isSignupModalOpen, setIsSignupModalOpen] = React.useState(false);

    const hasToolAccess = accessibleToolConfigNames.includes(tool.configName);
    const handleMakeCopyClicked = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideCoeditorMakeCopySelected",
            eventSchemaVersion: 1,
            documentId: documentID,
            aiGuideExperienceReportingLabelId: tool.reportingLabelId,
        });
        if (hasToolAccess) {
            copyDocumentMutation({documentID, snapshotID})
                .then((result) => {
                    const newDocumentID =
                        result.status === "success" &&
                        result.data.copyCoeditingDocument.newDocumentID;
                    if (newDocumentID) {
                        history.push(
                            createResumeDocumentHref(
                                tool.urlSlug,
                                newDocumentID,
                                persona,
                            ) + "&copied=true",
                        );
                    }
                })
                .catch(() => {
                    renderErrorToast(
                        t`Oops! Something went wrong. Please try again.`,
                    );
                });
        } else {
            setIsSignupModalOpen(true);
        }
    };

    const handleSignupClosed = (success: boolean) => {
        setIsSignupModalOpen(false);
        // Force a reload of the page to update the user's Khanmigo status.
        if (success) {
            reload();
        }
    };

    const showMakeCopyButton =
        PermissionsInfo.isLoggedIn &&
        (accessibleToolConfigNames.length > 0 || isKhanmigoEligible);

    const {queryParams} = useLocation();
    const platform =
        queryParams.platform === "Canvas"
            ? Platform.Canvas
            : Platform.KhanAcademy;

    return (
        <View style={styles.container}>
            <AIToolHeader
                tool={tool}
                resetDocumentAndChat={() => {}}
                onSelectPreferences={() => {}}
                viewOnly={true}
                savingStatus="idle"
                showMakeCopyButton={showMakeCopyButton}
                handleMakeCopyClicked={
                    hasToolAccess || isKhanmigoEligible
                        ? handleMakeCopyClicked
                        : undefined
                }
                platform={platform}
            />
            <View style={styles.coeditorWrapper}>
                <Coeditor onChatSuggestionClicked={() => {}} viewOnly={true} />
            </View>
            {isKhanmigoEligible && (
                <TeacherToolsSignupPopup
                    opened={isSignupModalOpen}
                    onClose={(success) => handleSignupClosed(!!success)}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    coeditorWrapper: {
        backgroundColor: color.white,
        border: `1px solid ${color.offBlack8}`,
        borderRadius: spacing.xxxSmall_4,
        margin: `${spacing.xxxLarge_64}px auto 0`,
        maxWidth: TOOL_MAX_WIDTH,
        width: `calc(100% - ${spacing.xLarge_32}px)`,
        // 200px is the height of the header + 128px margin for short docs
        minHeight: `calc(100% - 200px)`,
    },
    container: {
        backgroundColor: color.offWhite,
        height: "100%",
    },
});

export default compose(
    withConversionContext({
        activity: enums.Activity.NAVIGATING,
    }),
)(TeacherDocumentView) as React.ComponentType<Props>;
